import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageView } from "../../../components/page/page-view";
import { Button } from "../../../components/ui/button";
import { Spinner } from "../../../components/ui/spinner";
import { ApiError } from "../../../lib/api/client";
import { errorMessage } from "../../../lib/api/messages";
import { useBodyGeneration } from "../../../lib/notion/mutations";
import { pageQuery } from "../../../lib/notion/queries";

/** A page or a board. The editor is keyed on the page, so moving between pages never mixes their bodies. */
export const Route = createFileRoute("/_app/p/$pageId")({
	component: PageScreen,
});

function PageScreen() {
	const { t } = useTranslation("pages");
	const { pageId } = Route.useParams();
	const page = useQuery(pageQuery(pageId));
	const generation = useBodyGeneration(pageId);

	if (page.isPending) {
		return (
			<div className="flex items-center gap-[0.5rem] px-[3rem] py-[4rem] text-muted">
				<Spinner className="size-[1.25rem]" />
				{t("loading")}
			</div>
		);
	}
	if (page.isError) {
		const missing = page.error instanceof ApiError && page.error.code === "NOT_FOUND";
		return (
			<div className="mx-auto flex max-w-[40rem] flex-col items-start gap-[1rem] px-[1.5rem] py-[4rem]">
				<h1 className="font-black text-[1.75rem]">{missing ? t("missing.title") : t("loadError")}</h1>
				<p className="text-muted">{missing ? t("missing.body") : errorMessage(page.error)}</p>
				{missing ? (
					<Link to="/" className="font-bold text-board underline underline-offset-4">
						{t("missing.home")}
					</Link>
				) : (
					<Button onClick={() => void page.refetch()}>{t("retry")}</Button>
				)}
			</div>
		);
	}
	// A restore from history replaces the body: a new generation rebuilds the editor (and its autosave) from it.
	return <PageView key={`${pageId}:${generation}`} page={page.data} />;
}
